import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, type UseMutationResult } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  DownloadCloud,
  FilePenLine,
  HelpCircle,
  Info,
  Loader2,
  Pencil,
  RefreshCw,
  Send,
  X,
} from 'lucide-react';
import { Spinner, TableSkeleton } from '@components/medicus-ui/Loaders';
import { UploadDropzone } from '@components/UploadDropzone';
import { AyudaModificacionesModal } from '@components/AyudaModificacionesModal';
import {
  confirmarLote,
  descargarArchivoBinary,
  descargarPlantillaModificaciones,
  corregirNovedad,
  descargarReporte,
  getLote,
  listarLotes,
  listarNovedades,
  previewModificaciones,
  reintentarLote,
  type LoteResumen,
  type ModificacionItem,
  type PreviewModificaciones,
  type ResumenModificaciones,
} from '@lib/services/modificaciones.service';
import {
  estadoUi,
  etiquetaClase,
  labelCampo,
  type ClaseModificacion,
} from '@lib/catalogs/campos-modificables';
import { filtrar, puedeExcluirse, type FiltroModificacion } from '@lib/modificaciones-filtro';
import {
  aEnviar as calcularAEnviar,
  textoConfirmar,
  textoExclusiones,
} from '@lib/modificaciones-confirmar';
import { useAuthStore } from '@lib/store/auth.store';
import { useVistoUnaVez } from '@lib/hooks/useVistoUnaVez';
import { puedeVer } from '@lib/permissions/catalog';

function mensajeError(e: unknown): string {
  const resp = (e as { response?: { data?: { message?: string; error?: { message?: string } } } })
    ?.response;
  return resp?.data?.error?.message ?? resp?.data?.message ?? 'Ocurrió un error.';
}

/**
 * Sección "Modificación de datos personales" (requerimiento F2-6).
 *
 * UN item de menú con DOS tabs, no dos secciones: el sidebar del proyecto es una
 * lista plana y CA-001 pide ver "la opción ... con separación entre Datos
 * Sensibles y Datos No Sensibles".
 *
 * Cada tab tiene su propia clave de permiso de CARGA. Ver la sección y los
 * trámites lo habilita `view:modificaciones`, que también tiene Medicus para
 * poder seguirlos (RF-015 / CA-012).
 */
export function ModificacionesPage() {
  const [tab, setTab] = useState<ClaseModificacion>('sensibles');
  const user = useAuthStore((s) => s.user);
  // Igual que en Bajas: la ayuda se abre SOLA la primera vez que este usuario
  // entra a la sección y después queda a mano en el signo de pregunta. Abrirla
  // siempre sería ruido; no abrirla nunca dejaría la documentación escondida.
  const { visto, marcarVisto } = useVistoUnaVez('ayuda-modificaciones', user?.id);
  const [ayudaAbierta, setAyudaAbierta] = useState(!visto);

  const cerrarAyuda = () => {
    setAyudaAbierta(false);
    marcarVisto();
  };

  return (
    <section>
      <header className="pageHeader">
        <div className="rowBetween" style={{ alignItems: 'flex-start', gap: 12 }}>
          <h1 className="heading-3" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FilePenLine size={22} style={{ color: '#023671' }} /> Modificación de datos personales
          </h1>
          <button
            type="button"
            className="btn btn-ghost btn--sm"
            onClick={() => setAyudaAbierta(true)}
            title="Cómo funciona la modificación de datos"
            aria-label="Abrir la ayuda de la sección Modificación de datos"
            style={{ display: 'flex', alignItems: 'center', gap: 6, flex: '0 0 auto' }}
          >
            <HelpCircle size={18} />
            <span>Ayuda</span>
          </button>
        </div>
        <p className="text-element-mid">
          Corregir datos de titulares e integrantes ya dados de alta a partir de un archivo. Se
          valida cada modificación contra el padrón, contra RENAPER cuando corresponde y contra
          Binary en vivo. Las filas con error se excluyen y el resto sigue.
        </p>
      </header>

      {ayudaAbierta && <AyudaModificacionesModal onClose={cerrarAyuda} />}

      <div
        role="tablist"
        aria-label="Tipo de datos a modificar"
        style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}
      >
        {(['sensibles', 'no_sensibles'] as const).map((c) => (
          <button
            key={c}
            type="button"
            role="tab"
            aria-selected={tab === c}
            className={tab === c ? 'btn btn-primary' : 'btn btn-ghost'}
            onClick={() => setTab(c)}
          >
            {etiquetaClase(c)}
          </button>
        ))}
      </div>

      {/* `key` fuerza el remount al cambiar de tab: si no, el resultado del
          archivo anterior quedaría en pantalla bajo el título del otro flujo. */}
      <FlujoModificacion key={tab} clase={tab} />
      <UltimosTramites clase={tab} />
    </section>
  );
}

// ===========================================================================

function FlujoModificacion({ clase }: { clase: ClaseModificacion }) {
  const user = useAuthStore((s) => s.user);
  const permisoCarga =
    clase === 'sensibles' ? 'view:modificaciones-sensibles' : 'view:modificaciones-no-sensibles';
  const puedeCargar = !!user && puedeVer(user, permisoCarga);

  const [preview, setPreview] = useState<PreviewModificaciones | null>(null);
  const [confirmado, setConfirmado] = useState<LoteResumen | null>(null);
  const [error, setError] = useState<string | null>(null);

  const subir = useMutation({
    mutationFn: (file: File) => previewModificaciones(file, clase),
    onSuccess: (d) => {
      setPreview(d);
      setConfirmado(null);
      setError(null);
    },
    onError: (e) => {
      setPreview(null);
      setError(mensajeError(e));
    },
  });

  const descargar = useMutation({
    mutationFn: () => descargarPlantillaModificaciones(clase),
    onError: (e) => setError(mensajeError(e)),
  });

  return (
    <>
      <div className="card" style={{ marginBottom: 20 }}>
        <h2 className="heading-4" style={{ marginTop: 0 }}>
          1. Descargá la plantilla
        </h2>
        <p className="text-element-mid">
          Trae las columnas de {etiquetaClase(clase)}, los valores permitidos y qué queda fuera de
          alcance. Dejá vacías las columnas que no quieras modificar.
        </p>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => descargar.mutate()}
          disabled={descargar.isPending}
          style={{ display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <DownloadCloud size={18} />
          {descargar.isPending ? 'Descargando…' : `Plantilla de ${etiquetaClase(clase)}`}
        </button>
      </div>

      <div className="card">
        <h2 className="heading-4" style={{ marginTop: 0 }}>
          2. Subí el archivo completado
        </h2>
        {puedeCargar ? (
          <UploadDropzone
            onFile={(f) => subir.mutate(f)}
            disabled={subir.isPending}
            allowed={['.xlsx', '.xls', '.xlsm', '.csv']}
            hint="No se modifica nada todavía: primero se valida y se muestra el resultado."
          />
        ) : (
          <p className="status status-error">
            Tu usuario puede ver esta sección pero no cargar archivos de {etiquetaClase(clase)}.
          </p>
        )}
        {subir.isPending && (
          <p className="text-element-mid" style={{ marginTop: 12 }}>
            Validando el archivo…
          </p>
        )}
        {error && (
          <p className="status status-error" style={{ marginTop: 12 }}>
            {error}
          </p>
        )}
      </div>

      {preview && (
        <Resultado
          preview={preview}
          puedeConfirmar={puedeCargar}
          confirmado={confirmado}
          onConfirmado={setConfirmado}
        />
      )}
    </>
  );
}

// ===========================================================================

/**
 * Botón de descarga con su estado: spinner mientras baja, aviso si falló.
 * Afuera de `Resultado` para no sumarle condicionales (Sonar S3776).
 */
function BotonDescarga({
  descarga,
  id,
  label,
  errorLabel,
}: {
  descarga: UseMutationResult<void, unknown, string>;
  id: string;
  label: string;
  errorLabel: string;
}) {
  return (
    <>
      <button
        type="button"
        className="btn btn-ghost"
        onClick={() => descarga.mutate(id)}
        disabled={descarga.isPending}
        style={{ display: 'flex', alignItems: 'center', gap: 8 }}
      >
        {descarga.isPending ? <Spinner size={16} /> : <DownloadCloud size={18} />}
        {descarga.isPending ? 'Descargando…' : label}
      </button>
      {descarga.isError && <span className="status status-error">{errorLabel}</span>}
    </>
  );
}

function Resultado({
  preview,
  puedeConfirmar,
  confirmado,
  onConfirmado,
}: {
  preview: PreviewModificaciones;
  puedeConfirmar: boolean;
  confirmado: LoteResumen | null;
  onConfirmado: (l: LoteResumen) => void;
}) {
  const [filtro, setFiltro] = useState<FiltroModificacion>('todas');
  const [excluidos, setExcluidos] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const r: ResumenModificaciones = confirmado?.resumen ?? preview.resumen;

  // Mientras Binary procesa, el trámite se relee solo: el despacho corre en
  // background y sin esto la pantalla se queda esperando para siempre.
  //
  // Arranca apenas se confirma, no cuando el trámite dice `procesando`: el
  // despacho es fire-and-forget, así que la respuesta del confirmar todavía
  // dice `confirmado` y esperar a ver `procesando` sería esperar para siempre.
  // El intervalo se apaga solo cuando el trámite llega a un estado final.
  const seguimiento = useQuery({
    queryKey: ['modificaciones', 'lote', preview.loteId],
    queryFn: () => getLote(preview.loteId),
    enabled: !!confirmado,
    refetchInterval: (q) =>
      q.state.data && ['confirmado', 'procesando'].includes(q.state.data.estado) ? 3000 : false,
  });
  const lote = seguimiento.data ?? confirmado;
  const despachado =
    !!lote && ['procesado', 'procesado_con_errores', 'error'].includes(lote.estado);
  /** Contadores vivos: los del lote releído si el despacho ya corrió. */
  const rv: ResumenModificaciones = lote?.resumen ?? r;

  // Cuando el archivo es grande, la respuesta del preview trae solo las
  // primeras: el resto se pide paginado al trámite ya guardado.
  const pagina = useQuery({
    // El estado del trámite entra en la clave a propósito: cuando el despacho
    // termina, las filas cambiaron de estado y hay que releerlas.
    queryKey: ['modificaciones', preview.loteId, filtro, lote?.estado],
    queryFn: () =>
      listarNovedades(preview.loteId, {
        pageSize: 200,
        ...(filtro === 'todas' ? {} : filtro === 'rechazadas' ? { soloErrores: true } : { estado: filtro }),
      }),
    enabled: preview.hayMas || despachado,
  });

  const items = useMemo(
    () =>
      preview.hayMas || despachado
        ? (pagina.data?.items ?? [])
        : filtrar(preview.items, filtro),
    [preview.hayMas, despachado, preview.items, pagina.data, filtro],
  );

  // Descargas como mutación y no como `void fn()`: así el botón muestra que
  // está trabajando y se deshabilita mientras tanto (antes, un doble click
  // bajaba dos veces y un fallo no se veía).
  const bajarReporte = useMutation({ mutationFn: descargarReporte });
  const bajarPlanilla = useMutation({ mutationFn: descargarArchivoBinary });

  const confirmar = useMutation({
    mutationFn: () => confirmarLote(preview.loteId, [...excluidos]),
    onSuccess: (l) => {
      onConfirmado(l);
      setError(null);
    },
    onError: (e) => setError(mensajeError(e)),
  });

  const reintento = useMutation({
    mutationFn: () => reintentarLote(preview.loteId),
    onSuccess: (l) => {
      onConfirmado(l);
      setError(null);
      seguimiento.refetch();
    },
    onError: (e) => setError(mensajeError(e)),
  });

  // Corregir una fila re-valida SOLO esa novedad en el server y devuelve su
  // estado nuevo. La tabla se repinta con lo que contestó, no con lo que el
  // usuario escribió: el veredicto siempre es del server.
  const [corregidas, setCorregidas] = useState<Record<string, ModificacionItem>>({});
  const corregir = useMutation({
    mutationFn: (v: { id: string; valor: string }) =>
      corregirNovedad(preview.loteId, v.id, v.valor),
    onSuccess: (it) => {
      if (it.id) setCorregidas((prev) => ({ ...prev, [it.id as string]: it }));
      setError(null);
      pagina.refetch();
    },
    onError: (e) => setError(mensajeError(e)),
  });

  const toggle = (id: string) => {
    setExcluidos((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });
  };

  const conteo = {
    aceptadas: r.aceptadas,
    rechazadas: r.rechazadas,
    excluidas: confirmado ? 0 : excluidos.size,
    sinCambio: r.sinCambio,
  };
  const aEnviar = calcularAEnviar(conteo);

  return (
    <div className="card" style={{ marginTop: 20 }}>
      <h2 className="heading-4" style={{ marginTop: 0 }}>
        3. Resultado de la validación
      </h2>
      <p className="text-element-mid" style={{ marginTop: -6 }}>
        {preview.nombreArchivo} · {preview.total} modificación(es) leída(s)
      </p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '16px 0' }}>
        <Contador label="Listas para enviar" valor={r.aceptadas} tono="ok" />
        <Contador label="Con error" valor={r.rechazadas} tono="error" />
        <Contador label="Sin cambio" valor={r.sinCambio} tono="neutro" />
        {(r.soloConsulta ?? 0) > 0 && (
          <Contador label="Solo consulta" valor={r.soloConsulta ?? 0} tono="neutro" />
        )}
        {r.errorInfra > 0 && (
          <Contador label="No se pudieron validar" valor={r.errorInfra} tono="espera" />
        )}
      </div>

      {/* Nada en silencio: lo que el archivo trajo y no se usó, se dice. */}
      {preview.diagnostico.length > 0 && (
        <div
          style={{
            background: '#f8ecd2',
            border: '1px solid #e6d3a3',
            borderRadius: 8,
            padding: '12px 14px',
            marginBottom: 16,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
            <AlertTriangle size={18} style={{ color: '#b45309' }} />
            Avisos sobre el archivo
          </div>
          <ul style={{ margin: '8px 0 0', paddingLeft: 24 }}>
            {preview.diagnostico.map((d) => (
              <li key={d} style={{ fontSize: 13 }}>
                {d}
              </li>
            ))}
          </ul>
        </div>
      )}

      {r.errorInfra > 0 && (
        <p className="text-element-mid" style={{ display: 'flex', gap: 8, fontSize: 13 }}>
          <Info size={16} />
          Las que dicen «No se pudo validar» no tienen el dato mal: no se pudo consultar Binary o
          RENAPER. Volvé a subir el archivo más tarde.
        </p>
      )}

      {lote && (
        <PanelDespacho
          lote={lote}
          onReintentar={() => reintento.mutate()}
          reintentando={reintento.isPending}
        />
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '12px 0' }}>
        {(
          [
            ['todas', `Todas (${preview.total})`],
            // Después del despacho ya no queda nada en `aceptada`: el chip se
            // reemplaza por el resultado real en vez de mostrar un contador
            // que no matchea ninguna fila.
            ...(despachado
              ? ([
                  ['aplicada', `Impactaron (${rv.aplicadas ?? 0})`],
                  ['fallada', `No impactaron (${rv.falladas ?? 0})`],
                ] as const)
              : ([['aceptada', `Listas (${r.aceptadas})`]] as const)),
            ['rechazadas', `Con error (${rv.rechazadas + (despachado ? (rv.falladas ?? 0) : 0)})`],
            ['sin_cambio', `Sin cambio (${r.sinCambio})`],
            ['error_infra', `Sin validar (${r.errorInfra})`],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            type="button"
            className={filtro === k ? 'btn btn-primary btn--sm' : 'btn btn-ghost btn--sm'}
            onClick={() => setFiltro(k)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="tableWrap" style={{ maxHeight: 640, overflow: 'auto' }}>
        <table className="dataTable dataTable--stickyHead dataTable--headContrast">
          <thead>
            <tr>
              {/* La casilla solo aparece si el trámite todavía se puede tocar. */}
              {!confirmado && <th style={{ width: 40 }} aria-label="Excluir" />}
              <th>Fila</th>
              <th>CUIL</th>
              <th>Campo</th>
              <th>Antes → Después</th>
              <th>Estado</th>
              <th>Motivo</th>
            </tr>
          </thead>
          <tbody>
            {items.map((crudo) => {
              const it = (crudo.id && corregidas[crudo.id]) || crudo;
              return (
              <Fila
                key={crudo.id ?? `${crudo.fila}-${crudo.campo}-${crudo.cuilArchivo}`}
                it={it}
                excluido={!!it.id && excluidos.has(it.id)}
                puedeExcluir={!confirmado && puedeExcluirse(it)}
                mostrarCasilla={!confirmado}
                onToggle={toggle}
                puedeCorregir={!confirmado && puedeConfirmar && !!it.id}
                corrigiendo={corregir.isPending}
                onCorregir={(valor) => it.id && corregir.mutate({ id: it.id, valor })}
              />
              );
            })}
            {items.length === 0 && (
              <tr>
                <td colSpan={confirmado ? 6 : 7} className="text-element-mid" style={{ textAlign: 'center' }}>
                  {pagina.isLoading ? 'Cargando…' : 'No hay modificaciones con ese filtro.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {error && (
        <p className="status status-error" style={{ marginTop: 12 }}>
          {error}
        </p>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 18, alignItems: 'center' }}>
        <BotonDescarga
          descarga={bajarReporte}
          id={preview.loteId}
          label="Descargar reporte"
          errorLabel="No se pudo descargar el reporte."
        />

        {confirmado ? (
          <>
            {/* La planilla sigue disponible aunque el envío automático esté
                encendido: si Binary rechazó algo, Administración lo carga a
                mano con esto mismo. */}
            <BotonDescarga
              descarga={bajarPlanilla}
              id={preview.loteId}
              label="Planilla para Administración"
              errorLabel="No se pudo descargar la planilla."
            />
          </>
        ) : (
          puedeConfirmar && (
            <button
              type="button"
              className="btn btn-primary"
              disabled={confirmar.isPending || aEnviar <= 0}
              onClick={() => confirmar.mutate()}
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <Send size={18} />
              {confirmar.isPending
                ? 'Confirmando…'
                : textoConfirmar(conteo)}
            </button>
          )
        )}
      </div>

      {!confirmado && (
        <p className="text-element-mid" style={{ marginTop: 12, fontSize: 13 }}>
          {textoExclusiones(conteo)}
        </p>
      )}
    </div>
  );
}

/**
 * Qué pasó con el trámite después de confirmarlo (RF-013).
 *
 * Con el envío automático apagado el trámite queda confirmado y se sigue con la
 * planilla: se dice explícitamente en vez de dejar la pantalla en silencio,
 * porque el usuario no tiene otra forma de saber que Binary no se enteró.
 */
function PanelDespacho({
  lote,
  onReintentar,
  reintentando,
}: {
  lote: LoteResumen;
  onReintentar?: () => void;
  reintentando?: boolean;
}) {
  const aplicadas = lote.resumen.aplicadas ?? 0;
  const falladas = lote.resumen.falladas ?? 0;

  const caja = (bg: string, borde: string, contenido: ReactNode) => (
    <div
      style={{
        background: bg,
        border: `1px solid ${borde}`,
        borderRadius: 8,
        padding: '12px 14px',
        margin: '16px 0',
      }}
    >
      {contenido}
    </div>
  );

  // `confirmado` es el instante previo al despacho: el job corre en background y
  // todavía no movió el estado. Se muestra igual que «procesando» para no decir
  // «no impactó» de algo que está saliendo en este momento.
  if (lote.estado === 'confirmado' || lote.estado === 'procesando') {
    return caja(
      '#eef2f7',
      '#cbd5e1',
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
        <Loader2 size={18} style={{ flexShrink: 0, animation: 'spin 1s linear infinite' }} />
        <span>
          Enviando {lote.resumen.aceptadas} modificación(es) a Binary. Podés cerrar la pantalla: el
          trámite sigue solo y queda en el listado de abajo.
        </span>
      </div>,
    );
  }

  // El servicio no estuvo disponible. NO es que el dato esté mal: lo pendiente
  // sigue en «Listas para enviar» y el reintento lo retoma desde donde quedó,
  // sin volver a subir el archivo.
  if (lote.estado === 'error') {
    return caja(
      '#f8ecd2',
      '#e6d3a3',
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <AlertTriangle size={18} style={{ color: '#b45309', marginTop: 2, flexShrink: 0 }} />
        <div style={{ fontSize: 13, flex: 1 }}>
          <strong>No se pudo enviar a Binary. Probá de nuevo en unos minutos.</strong>
          <div style={{ marginTop: 2 }}>{lote.error ?? 'Sin detalle.'}</div>
          <div className="text-element-mid" style={{ marginTop: 4 }}>
            No hace falta volver a subir el archivo: lo que no salió quedó pendiente y el reintento
            lo retoma. Lo que ya impactó no se manda de nuevo.
          </div>
          {onReintentar && (
            <button
              type="button"
              className="btn btn-primary btn--sm"
              onClick={onReintentar}
              disabled={reintentando}
              style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10 }}
            >
              <RefreshCw size={16} className={reintentando ? 'spin' : undefined} />
              {reintentando ? 'Reintentando…' : 'Reintentar el envío'}
            </button>
          )}
        </div>
      </div>,
    );
  }

  const conErrores = lote.estado === 'procesado_con_errores';
  return caja(
    conErrores ? '#f8ecd2' : '#e7f5ec',
    conErrores ? '#e6d3a3' : '#a9d8bd',
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      {conErrores ? (
        <AlertTriangle size={18} style={{ color: '#b45309', marginTop: 2, flexShrink: 0 }} />
      ) : (
        <CheckCircle2 size={18} style={{ color: '#15803d', marginTop: 2, flexShrink: 0 }} />
      )}
      <div style={{ fontSize: 13 }}>
        <strong>
          {aplicadas} modificación(es) impactaron en Binary
          {falladas > 0 ? `, ${falladas} no` : ''}.
        </strong>
        {falladas > 0 && (
          <div className="text-element-mid" style={{ marginTop: 2 }}>
            Las que no impactaron tienen el motivo de Binary en su fila. Corregilas y volvé a
            subirlas: las que ya se aplicaron no se vuelven a enviar.
          </div>
        )}
      </div>
    </div>,
  );
}

function Fila({
  it,
  excluido,
  puedeExcluir,
  mostrarCasilla,
  onToggle,
  puedeCorregir,
  corrigiendo,
  onCorregir,
}: {
  it: ModificacionItem;
  excluido: boolean;
  puedeExcluir: boolean;
  mostrarCasilla: boolean;
  onToggle: (id: string) => void;
  puedeCorregir?: boolean;
  corrigiendo?: boolean;
  onCorregir?: (valor: string) => void;
}) {
  const ui = estadoUi(it.estado);
  const [editando, setEditando] = useState(false);
  const [borrador, setBorrador] = useState(it.valorNuevo || it.valorNuevoCrudo || '');

  const guardar = () => {
    setEditando(false);
    if (borrador.trim() !== (it.valorNuevo || it.valorNuevoCrudo || '').trim()) {
      onCorregir?.(borrador.trim());
    }
  };
  return (
    <tr style={excluido ? { opacity: 0.5 } : undefined}>
      {mostrarCasilla && (
        <td>
          {puedeExcluir && (
            <input
              type="checkbox"
              checked={excluido}
              onChange={() => it.id && onToggle(it.id)}
              aria-label={`Excluir la fila ${it.fila}`}
            />
          )}
        </td>
      )}
      <td>{it.fila}</td>
      <td style={{ whiteSpace: 'nowrap' }}>{it.cuilArchivo || '—'}</td>
      <td>{labelCampo(it.campo)}</td>
      <td>
        <span className="text-element-mid">{it.valorAnterior || '—'}</span>
        {' → '}
        {editando ? (
          // Se corrige acá mismo en vez de volver a subir el archivo: el server
          // re-valida esa sola fila y devuelve el veredicto.
          <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
            <input
              autoFocus
              value={borrador}
              onChange={(e) => setBorrador(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') guardar();
                if (e.key === 'Escape') setEditando(false);
              }}
              aria-label={`Corregir ${labelCampo(it.campo)} de la fila ${it.fila}`}
              style={{ width: 150, padding: '2px 6px', fontSize: 13 }}
            />
            <button
              type="button"
              className="btn btn-primary btn--sm"
              onClick={guardar}
              aria-label="Guardar la corrección"
            >
              OK
            </button>
            <button
              type="button"
              className="btn btn-ghost btn--sm"
              onClick={() => setEditando(false)}
              aria-label="Cancelar la corrección"
            >
              <X size={14} />
            </button>
          </span>
        ) : (
          <>
            <strong>{it.valorNuevo || it.valorNuevoCrudo || '—'}</strong>
            {puedeCorregir && (
              <button
                type="button"
                className="btn btn-ghost btn--sm"
                onClick={() => {
                  setBorrador(it.valorNuevo || it.valorNuevoCrudo || '');
                  setEditando(true);
                }}
                disabled={corrigiendo}
                title="Corregir este valor sin volver a subir el archivo"
                aria-label={`Corregir ${labelCampo(it.campo)} de la fila ${it.fila}`}
                style={{ marginLeft: 6, padding: '0 4px' }}
              >
                <Pencil size={13} />
              </button>
            )}
          </>
        )}
      </td>
      <td>
        <span
          style={{
            display: 'inline-block',
            padding: '2px 10px',
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            color: ui.color,
            background: ui.bg,
          }}
        >
          {excluido ? 'Excluida' : ui.label}
        </span>
      </td>
      <td style={{ fontSize: 13 }}>
        {it.motivo || '—'}
        {/* El valor oficial de RENAPER es lo que el usuario necesita para
            corregir: sin él, "no coincide" es inaccionable. */}
        {it.renaper?.valorRenaper && !it.renaper.ok && (
          <div className="text-element-mid" style={{ fontSize: 12, marginTop: 2 }}>
            RENAPER dice: <strong>{it.renaper.valorRenaper}</strong>
          </div>
        )}
        {/* El nº de solicitud es con lo que se busca el trámite en Binary:
            sin él, reclamar por un registro puntual es imposible. */}
        {it.binary?.solafiId && (
          <div className="text-element-mid" style={{ fontSize: 12, marginTop: 2 }}>
            Solicitud Binary <strong>{it.binary.solafiId}</strong>
            {it.binary.vdaDrv ? ` · estado ${it.binary.vdaDrv}` : ''}
          </div>
        )}
      </td>
    </tr>
  );
}

// ===========================================================================

/** Trámites anteriores del flujo, para seguimiento (RF-015 / CA-011). */
function UltimosTramites({ clase }: { clase: ClaseModificacion }) {
  const { data, isLoading } = useQuery({
    queryKey: ['modificaciones-lotes', clase],
    queryFn: () => listarLotes({ clase, pageSize: 10 }),
  });

  const bajar = useMutation({ mutationFn: descargarReporte });

  // Mientras carga se ve la card con un placeholder: antes devolvía `null` y
  // la sección aparecía de golpe debajo del flujo.
  if (isLoading) {
    return (
      <div className="card" style={{ marginTop: 20 }}>
        <h2 className="heading-4" style={{ marginTop: 0 }}>
          Trámites de {etiquetaClase(clase)}
        </h2>
        <TableSkeleton rows={3} />
      </div>
    );
  }
  if (!data || data.items.length === 0) return null;

  return (
    <div className="card" style={{ marginTop: 20 }}>
      <h2 className="heading-4" style={{ marginTop: 0 }}>
        Trámites de {etiquetaClase(clase)}
      </h2>
      <div className="tableWrap">
        <table className="dataTable">
          <thead>
            <tr>
              <th>Archivo</th>
              <th>Fecha</th>
              <th>Cargado por</th>
              <th>Estado</th>
              <th>Listas</th>
              <th>Con error</th>
              <th>Impactaron</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.items.map((l) => (
              <tr key={l.id}>
                <td>{l.nombreArchivo}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {new Date(l.createdAt).toLocaleString('es-AR')}
                </td>
                <td>{l.subidoPor}</td>
                <td>{ESTADO_LOTE_LABEL[l.estado] ?? l.estado}</td>
                <td>{l.resumen?.aceptadas ?? 0}</td>
                <td>{l.resumen?.rechazadas ?? 0}</td>
                {/* Vacío mientras el trámite no se despachó: un 0 se leería
                    como «Binary lo rechazó todo». */}
                <td style={{ whiteSpace: 'nowrap' }}>
                  {l.finishedAt
                    ? `${l.resumen?.aplicadas ?? 0}${(l.resumen?.falladas ?? 0) > 0 ? ` · ${l.resumen?.falladas ?? 0} no` : ''}`
                    : '—'}
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn-ghost btn--sm"
                    disabled={bajar.isPending && bajar.variables === l.id}
                    onClick={() => bajar.mutate(l.id)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    {bajar.isPending && bajar.variables === l.id && <Spinner size={13} />}
                    Reporte
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const ESTADO_LOTE_LABEL: Record<string, string> = {
  validado: 'Validado, sin confirmar',
  confirmado: 'Confirmado',
  procesando: 'Procesando',
  procesado: 'Procesado',
  procesado_con_errores: 'Procesado con errores',
  error: 'Error',
};

function Contador({
  label,
  valor,
  tono,
}: {
  label: string;
  valor: number;
  tono: 'ok' | 'neutro' | 'error' | 'espera';
}) {
  const colores = {
    ok: { color: '#166534', bg: '#e2f3e8' },
    neutro: { color: '#6b7280', bg: '#f1f3f5' },
    error: { color: '#b91c1c', bg: '#f9e2e5' },
    espera: { color: '#b45309', bg: '#f8ecd2' },
  }[tono];
  return (
    <div
      style={{
        padding: '10px 16px',
        borderRadius: 8,
        background: colores.bg,
        color: colores.color,
        minWidth: 120,
      }}
    >
      <div style={{ fontSize: 22, fontWeight: 700 }}>{valor}</div>
      <div style={{ fontSize: 12 }}>{label}</div>
    </div>
  );
}
